import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getMediaList, deleteMedia, type MediaItem } from '../../shared/api/media';
import { useAuth } from '../../shared/auth/AuthContext';
import { getAdminLoginPath } from '../../shared/config/env';
import AdminSidebar from '../components/AdminSidebar';
import './MediaList.css';

function MediaList() {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [hasNext, setHasNext] = useState<boolean>(false);
  const [hasPrev, setHasPrev] = useState<boolean>(false);
  const [mediaTypeFilter, setMediaTypeFilter] = useState<'image' | 'video' | undefined>(undefined);
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [deletingMediaId, setDeletingMediaId] = useState<string | null>(null);
  const [selectedMediaIds, setSelectedMediaIds] = useState<Set<string>>(new Set());
  const [isDeletingMultiple, setIsDeletingMultiple] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const adminLoginPath = getAdminLoginPath();

  const fetchMedia = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getMediaList({
        page,
        per_page: 20,
        media_type: mediaTypeFilter,
        search: searchQuery.trim() || undefined,
      });
      setMedia(response.media);
      setTotalPages(response.meta.total_pages);
      setTotal(response.meta.total);
      setHasNext(response.meta.has_next);
      setHasPrev(response.meta.has_prev);
    } catch (err) {
      if (err instanceof Error && (err as any).isAuthError) {
        // 認証エラーの場合、ログアウトしてログイン画面にリダイレクト
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        // CORSエラーや500エラーの場合、より分かりやすいメッセージを表示
        let errorMessage = err.message;
        if (errorMessage.includes('CORS') || errorMessage.includes('Failed to fetch')) {
          errorMessage = 'API GatewayのCORS設定またはエンドポイントの設定を確認してください。メディア一覧取得APIが実装されていない可能性があります。';
        } else if (errorMessage.includes('500')) {
          errorMessage = 'サーバーエラーが発生しました。メディア一覧取得APIの実装を確認してください。';
        }
        setError(errorMessage);
      } else {
        setError('メディアの取得に失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia();
  }, [page, mediaTypeFilter, searchQuery]);

  const handleSearch = () => {
    setSearchQuery(searchInput.trim());
    setPage(1);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) {
      return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMediaUrl = (item: MediaItem): string => {
    return item.cloudfront_url || item.s3_url;
  };

  const handleCopyUrl = async (item: MediaItem) => {
    const url = getMediaUrl(item);
    if (!url) {
      setToastMessage('URLが取得できませんでした');
      setTimeout(() => setToastMessage(null), 3000);
      return;
    }
    
    // 記事作成画面と同じ形式でMarkdownテキストを生成
    const filename = item.original_filename;
    const imageMarkdown = `![${filename}](${url})`;
    
    try {
      await navigator.clipboard.writeText(imageMarkdown);
      setToastMessage('✓ 画像タグをクリップボードにコピーしました');
      setTimeout(() => setToastMessage(null), 3000);
    } catch (err) {
      setToastMessage('✗ 画像タグのコピーに失敗しました');
      setTimeout(() => setToastMessage(null), 3000);
    }
  };

  const handleDelete = async (mediaId: string, filename: string) => {
    if (!window.confirm(`「${filename}」を削除してもよろしいですか？\n\nこの操作は取り消せません。`)) {
      return;
    }

    try {
      setDeletingMediaId(mediaId);
      await deleteMedia(mediaId);
      // 削除成功後、一覧を再取得
      await fetchMedia();
      // 選択状態からも削除
      setSelectedMediaIds((prev) => {
        const next = new Set(prev);
        next.delete(mediaId);
        return next;
      });
    } catch (err) {
      console.error('Delete media error:', err);
      if (err instanceof Error && (err as any).isAuthError) {
        // 認証エラーの場合、ログアウトしてログイン画面にリダイレクト
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        alert(`削除に失敗しました: ${err.message}`);
      } else {
        alert('削除に失敗しました');
      }
    } finally {
      setDeletingMediaId(null);
    }
  };

  const handleToggleSelect = (mediaId: string) => {
    setSelectedMediaIds((prev) => {
      const next = new Set(prev);
      if (next.has(mediaId)) {
        next.delete(mediaId);
      } else {
        next.add(mediaId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const allSelectableIds = media
      .filter((item) => item.used_count === 0)
      .map((item) => item.media_id);
    
    if (allSelectableIds.every((id) => selectedMediaIds.has(id))) {
      // すべて選択されている場合は、すべて解除
      setSelectedMediaIds(new Set());
    } else {
      // すべて選択
      setSelectedMediaIds(new Set(allSelectableIds));
    }
  };

  const handleDeleteMultiple = async () => {
    if (selectedMediaIds.size === 0) {
      return;
    }

    const selectedItems = media.filter((item) => selectedMediaIds.has(item.media_id));
    const filenames = selectedItems.map((item) => item.original_filename).join('、');
    
    if (!window.confirm(`以下の${selectedMediaIds.size}件のメディアを削除してもよろしいですか？\n\n${filenames}\n\nこの操作は取り消せません。`)) {
      return;
    }

    try {
      setIsDeletingMultiple(true);
      const deletePromises = Array.from(selectedMediaIds).map((mediaId) => deleteMedia(mediaId));
      await Promise.all(deletePromises);
      // 削除成功後、一覧を再取得
      await fetchMedia();
      setSelectedMediaIds(new Set());
    } catch (err) {
      console.error('Delete multiple media error:', err);
      if (err instanceof Error && (err as any).isAuthError) {
        // 認証エラーの場合、ログアウトしてログイン画面にリダイレクト
        logout();
        navigate(adminLoginPath, { replace: true });
        return;
      }
      if (err instanceof Error) {
        alert(`削除に失敗しました: ${err.message}`);
      } else {
        alert('削除に失敗しました');
      }
    } finally {
      setIsDeletingMultiple(false);
    }
  };

  return (
    <div className="media-list-container">
      <AdminSidebar />
      <main className="media-list-main">
        <div className="media-list-content">
          <div className="media-list-header">
            <h1>メディア一覧</h1>
            <div className="header-actions">
              <a
                href="/admin/media"
                className="btn-upload-link"
              >
                アップロード
              </a>
            </div>
          </div>

          {error && (
            <div className="media-list-error">
              {error}
            </div>
          )}

          {toastMessage && (
            <div className="media-list-toast">
              {toastMessage}
            </div>
          )}

          {loading ? (
            <div className="media-list-loading">
              読み込み中...
            </div>
          ) : (
            <>
              <div className="media-list-filters">
                <div className="filter-group">
                  <label htmlFor="media-type-filter">種類:</label>
                  <select
                    id="media-type-filter"
                    value={mediaTypeFilter || ''}
                    onChange={(e) => {
                      setMediaTypeFilter(
                        e.target.value === '' ? undefined : (e.target.value as 'image' | 'video')
                      );
                      setPage(1);
                    }}
                    className="filter-select"
                  >
                    <option value="">すべて</option>
                    <option value="image">画像</option>
                    <option value="video">動画</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label htmlFor="search-input">検索:</label>
                  <input
                    id="search-input"
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={handleSearchKeyPress}
                    placeholder="ファイル名で検索"
                    className="filter-input"
                  />
                  <button
                    type="button"
                    onClick={handleSearch}
                    className="btn-search"
                  >
                    検索
                  </button>
                </div>
                <div className="filter-info">
                  全{total}件
                </div>
              </div>

              {selectedMediaIds.size > 0 && (
                <div className="media-list-bulk-actions">
                  <span className="bulk-actions-info">
                    {selectedMediaIds.size}件選択中
                  </span>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="btn-select-all"
                  >
                    すべて選択/解除
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteMultiple}
                    disabled={isDeletingMultiple}
                    className="btn-delete-multiple"
                  >
                    {isDeletingMultiple ? '削除中...' : `選択した${selectedMediaIds.size}件を削除`}
                  </button>
                </div>
              )}

              {media.length === 0 ? (
                <div className="media-list-empty">
                  メディアがありません
                </div>
              ) : (
                <>
                  <div className="media-list-grid">
                    {media.map((item) => (
                      <div key={item.media_id} className="media-item">
                        <div className="media-item-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedMediaIds.has(item.media_id)}
                            onChange={() => handleToggleSelect(item.media_id)}
                            disabled={item.used_count > 0 || isDeletingMultiple}
                            title={item.used_count > 0 ? '使用中のメディアは選択できません' : ''}
                          />
                        </div>
                        <div className="media-item-preview">
                          {item.media_type === 'image' ? (
                            <img
                              src={getMediaUrl(item)}
                              alt={item.original_filename}
                              className="media-item-image"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="media-item-video-placeholder">
                              <span className="video-icon">🎬</span>
                              <span className="video-label">動画</span>
                            </div>
                          )}
                        </div>
                        <div className="media-item-info">
                          <div className="media-item-filename" title={item.original_filename}>
                            <Link
                              to={`/admin/media/${item.media_id}`}
                              className="media-item-filename-link"
                            >
                              {item.original_filename}
                            </Link>
                          </div>
                          <div className="media-item-details">
                            <span className="media-item-type">
                              {item.media_type === 'image' ? '画像' : '動画'}
                            </span>
                            <span className="media-item-size">
                              {formatFileSize(item.file_size)}
                            </span>
                            <span className="media-item-date">
                              {formatDate(item.uploaded_at)}
                            </span>
                          </div>
                          <div className="media-item-actions">
                            <button
                              type="button"
                              onClick={() => handleCopyUrl(item)}
                              className="btn-copy-url"
                              title="画像タグをコピー"
                            >
                              コピー
                            </button>
                            <Link
                              to={`/admin/media/${item.media_id}`}
                              className="btn-view"
                              title="詳細を見る"
                            >
                              開く
                            </Link>
                            <button
                              type="button"
                              onClick={() => handleDelete(item.media_id, item.original_filename)}
                              disabled={deletingMediaId === item.media_id || item.used_count > 0}
                              className="btn-delete"
                              title={item.used_count > 0 ? '使用中のメディアは削除できません' : '削除'}
                            >
                              {deletingMediaId === item.media_id ? '削除中...' : '削除'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="media-list-pagination">
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={!hasPrev}
                        className="btn-pagination"
                      >
                        前へ
                      </button>
                      <span className="pagination-info">
                        {page} / {totalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={!hasNext}
                        className="btn-pagination"
                      >
                        次へ
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default MediaList;

